import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import { COLORS } from "../data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PrebookForm = ({ id, onSuccess, compact }) => {
  const [email, setEmail] = useState("");
  const [color, setColor] = useState("Black");
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [msg, setMsg] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setState("loading");
    setMsg("");
    try {
      await axios.post(`${API}/prebook`, { email, color });
      setState("done");
      onSuccess && onSuccess();
    } catch (err) {
      setState("error");
      setMsg(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
  };

  if (state === "done") {
    return (
      <div data-testid={`${id}-success`} className="flex items-start gap-4 py-6">
        <div className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#0A0A0A] text-white">
          <Check size={16} />
        </div>
        <div>
          <p className="font-serif-display text-2xl text-[#0A0A0A]">You're on the list.</p>
          <p className="mt-1 text-[#525252]">
            Your <span className="text-[#0A0A0A] font-medium">{color}</span> Transcendence is reserved at zero cost.
            We'll email <span className="text-[#0A0A0A]">{email}</span> the day orders open.
          </p>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid={`${id}-form`} className="w-full">
      <div className="mb-8">
        <p className="eyebrow mb-4">Choose your colour</p>
        <div className="flex flex-wrap gap-4" role="radiogroup" aria-label="Colour option">
          {COLORS.map((c) => {
            const active = color === c.name;
            return (
              <button
                key={c.name}
                type="button"
                onClick={() => setColor(c.name)}
                aria-label={c.name}
                aria-pressed={active}
                data-testid={`${id}-swatch-${c.name.toLowerCase()}`}
                className="group flex flex-col items-center gap-2 focus:outline-none"
              >
                <span
                  className={`h-9 w-9 rounded-full transition-transform duration-300 ${active ? "scale-110" : "group-hover:scale-105"}`}
                  style={{
                    background: c.swatch,
                    border: c.name === "White" ? "1px solid #d4d4d4" : "1px solid rgba(0,0,0,0.08)",
                    boxShadow: active ? "0 0 0 2px #FBFBF9, 0 0 0 4px #0A0A0A" : "none",
                  }}
                />
                <span className={`font-mono-accent text-[10px] tracking-wider ${active ? "text-[#0A0A0A]" : "text-[#A3A3A3]"}`}>
                  {c.name}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label htmlFor={`${id}-email`} className="eyebrow mb-3 block">Email address</label>
          <input
            id={`${id}-email`}
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            data-testid={`${id}-email-input`}
            className="w-full border-b border-[#d4d4d4] bg-transparent pb-3 text-lg text-[#0A0A0A] outline-none transition-colors placeholder:text-[#c4c4c4] focus:border-[#0A0A0A]"
          />
        </div>
        <motion.button
          type="submit"
          disabled={state === "loading"}
          whileTap={{ scale: 0.98 }}
          data-testid={`${id}-submit`}
          className="group inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-[#0A0A0A] px-8 py-4 text-sm font-medium tracking-wide text-white transition-colors hover:bg-[#333] disabled:opacity-60"
        >
          {state === "loading" ? <Loader2 size={16} className="animate-spin" /> : "Pre-book at zero cost"}
          {state !== "loading" && <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />}
        </motion.button>
      </div>

      {state === "error" && (
        <p data-testid={`${id}-error`} className="mt-3 font-mono-accent text-xs text-[#8B0000]">{msg}</p>
      )}
      {!compact && (
        <p className="mt-5 eyebrow">No payment · No spam · Shipping starts in September</p>
      )}
    </form>
  );
};
